import type {
    UseFormReturn,
    UseFormProps,
    RegisterOptions,
    Path,
    UseFormRegisterReturn,
    FieldValues,
    FieldError,
    FieldErrors
} from 'react-hook-form'
import { useForm as useReactHookForm, useFormContext as useReactHookFormContext } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { get } from 'lodash'
import { useTranslations } from 'next-intl'
import type { InputProps } from '@/shared/v2/shared/components/Input'
import { useEffect } from 'react'

type TranslationFunction = ReturnType<typeof useTranslations>

type ExtendedRegisterReturn<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends Path<TFieldValues> = Path<TFieldValues>
> = UseFormRegisterReturn<TFieldName> & {
    state: InputProps['state']
    message?: string
    required?: boolean
}

type CustomUseFormReturn<TFieldValues extends FieldValues = FieldValues> = Omit<
    UseFormReturn<TFieldValues>,
    'register'
> & {
    register: <TFieldName extends Path<TFieldValues>>(
        name: TFieldName,
        options?: RegisterOptions<TFieldValues, TFieldName>
    ) => ExtendedRegisterReturn<TFieldValues, TFieldName>
}

const getErrorMessage = (error: FieldError | undefined): string | undefined => {
    if (!error) return undefined
    return typeof error.message === 'string' ? error.message : undefined
}

const getBaseSchema = (schema: z.ZodTypeAny): z.ZodObject<z.ZodRawShape> | null => {
    if (schema instanceof z.ZodObject) {
        return schema
    }
    if (schema instanceof z.ZodEffects) {
        const innerSchema = getBaseSchema(schema.innerType())
        return innerSchema
    }
    return null
}

const getNestedSchema = (schema: z.ZodTypeAny, path: string): z.ZodTypeAny | null => {
    const baseSchema = getBaseSchema(schema)
    if (!baseSchema) return null

    const parts = path.split('.')
    let currentSchema: z.ZodTypeAny = baseSchema

    for (const part of parts) {
        if (currentSchema instanceof z.ZodObject) {
            currentSchema = currentSchema.shape[part]
        } else if (currentSchema instanceof z.ZodArray) {
            currentSchema = currentSchema.element
            continue
        } else if (currentSchema instanceof z.ZodEffects) {
            const innerSchema = getBaseSchema(currentSchema)
            if (!innerSchema) return null
            currentSchema = innerSchema.shape[part]
        } else {
            return null
        }

        if (!currentSchema) return null
    }

    return currentSchema
}

const hasRequiredMessage = (error: unknown): boolean =>
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.toLowerCase().includes('required')

const isFieldRequired = (
    fieldSchema: z.ZodTypeAny | null,
    fieldError: FieldError | undefined,
    formState: { errors: FieldErrors },
    fieldName: string
): boolean => {
    const isStaticallyRequired = fieldSchema ? !fieldSchema.isOptional?.() : false

    const hasRequiredError = fieldError?.message?.toLowerCase().includes('required')

    const fieldParts = fieldName.split('.')
    if (fieldParts.length > 1) {
        const groupName = fieldParts[0]
        const groupErrors = get(formState.errors, groupName)

        const isArrayField = fieldParts.some((part) => !isNaN(Number(part)))

        if (isArrayField && groupErrors && typeof groupErrors === 'object') {
            const arrayItemErrors = get(groupErrors, fieldParts.slice(1, -1).join('.'))
            if (arrayItemErrors && typeof arrayItemErrors === 'object') {
                const siblingHasRequiredError = Object.values(arrayItemErrors).some(hasRequiredMessage)
                if (siblingHasRequiredError) {
                    return true
                }
            }
        } else if (groupErrors && typeof groupErrors === 'object') {
            const siblingHasRequiredError = Object.values(groupErrors).some(hasRequiredMessage)
            if (siblingHasRequiredError) {
                return true
            }
        }
    }

    return isStaticallyRequired || Boolean(hasRequiredError)
}

export function useForm<TSchema extends z.ZodTypeAny>({
    schema,
    ...props
}: Omit<UseFormProps<z.infer<TSchema>>, 'resolver'> & {
    schema: TSchema
}): CustomUseFormReturn<z.infer<TSchema>> {
    const t = useTranslations('zod')
    const methods = useReactHookForm<z.infer<TSchema>>({
        ...props,
        resolver: zodResolver(schema)
    })

    useEffect(() => {
        if (schema) {
            const zodErrorMap = createCustomErrorMap(t)
            z.setErrorMap(zodErrorMap)
        }
    }, [t, schema])

    const enhancedRegister = <TFieldName extends Path<z.infer<TSchema>>>(
        name: TFieldName,
        options?: RegisterOptions<z.infer<TSchema>, TFieldName>
    ): ExtendedRegisterReturn<z.infer<TSchema>, TFieldName> => {
        const original = methods.register(name, options)
        const fieldSchema = getNestedSchema(schema, name)
        // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the value has this shape at runtime, as the code has always read it
        const fieldError = get(methods.formState.errors, name) as FieldError | undefined

        return {
            ...original,
            name,
            required: isFieldRequired(fieldSchema, fieldError, methods.formState, name),
            state: fieldError ? 'error' : 'default',
            message: getErrorMessage(fieldError)
        }
    }

    return {
        ...methods,
        register: enhancedRegister
    }
}

export function useFormContext<TSchema extends z.ZodTypeAny>({
    schema
}: {
    schema: TSchema
}): CustomUseFormReturn<z.infer<TSchema>> {
    const methods = useReactHookFormContext<z.infer<TSchema>>()

    const enhancedRegister = <TFieldName extends Path<z.infer<TSchema>>>(
        name: TFieldName,
        options?: RegisterOptions<z.infer<TSchema>, TFieldName>
    ): ExtendedRegisterReturn<z.infer<TSchema>, TFieldName> => {
        const original = methods.register(name, options)
        const fieldSchema = getNestedSchema(schema, name)
        // eslint-disable-next-line typescript/no-unsafe-type-assertion -- the value has this shape at runtime, as the code has always read it
        const fieldError = get(methods.formState.errors, name) as FieldError | undefined

        return {
            ...original,
            name,
            required: isFieldRequired(fieldSchema, fieldError, methods.formState, name),
            state: fieldError ? 'error' : 'default',
            message: getErrorMessage(fieldError)
        }
    }

    return {
        ...methods,
        register: enhancedRegister
    }
}

const hasInclusiveType = [
    'inclusive',
    'inclusive_one',
    'inclusive_other',
    'inclusive_with_path',
    'inclusive_with_path_one',
    'inclusive_with_path_other',
    'not_inclusive',
    'not_inclusive_one',
    'not_inclusive_other',
    'not_inclusive_with_path',
    'not_inclusive_with_path_one',
    'not_inclusive_with_path_other'
]

export const createCustomErrorMap = (t: TranslationFunction): z.ZodErrorMap => {
    return (issue, ctx) => {
        let key = ''
        const { code } = issue

        if (code === 'custom') {
            if (issue.params && 'code' in issue.params) {
                return {
                    message: t(issue.params.code, issue.params) ?? ctx.defaultError
                }
            }
        }

        if (code === 'too_small' && issue.type === 'string' && issue.minimum === 1) {
            return { message: t('invalid_type_received_undefined') }
        }

        if (code === 'invalid_type') {
            return { message: t('invalid_enum') }
        }

        const inclusiveType = Object.keys(issue).find((item) => hasInclusiveType.indexOf(item) !== -1)

        switch (code) {
            case 'too_big':
            case 'too_small':
                key = `${issue.code}.${issue.type}.${inclusiveType}`
                break
            case 'invalid_string':
                // eslint-disable-next-line typescript/no-base-to-string, typescript/restrict-template-expressions -- zod names a string check by its validation, and an object one prints as it always has
                key = `${issue.code}.${issue.validation}`
                break
            case 'custom':
            case 'invalid_arguments':
            case 'invalid_date':
            case 'invalid_enum_value':
            case 'invalid_intersection_types':
            case 'invalid_literal':
            case 'invalid_return_type':
            case 'invalid_union':
            case 'invalid_union_discriminator':
            case 'not_finite':
            case 'not_multiple_of':
            case 'unrecognized_keys':
            default:
                key = issue.code
        }
        // eslint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-type-assertion -- the value has this shape at runtime, as the code has always read it
        return { message: t(key, issue as any) ?? ctx.defaultError }
    }
}
